import * as styles from "./AmendmentSubscriptionsTab.css";

import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";

import { Badge } from "@frontend/components/atoms/Badge";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Panel } from "@frontend/components/atoms/Panel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { Pagination } from "@frontend/components/molecules/Pagination";
import {
  Toolbar,
  ToolbarSection,
} from "@frontend/components/molecules/Toolbar";
import {
  DataTable,
  createDataTableColumnHelper,
} from "@frontend/components/organisms/DataTable";
import type { ProjectRow } from "@frontend/features/projects";
import { useDebouncedCallback } from "@frontend/hooks/useDebouncedCallback";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  amendmentSubscriptionsAtom,
  amendmentSubscriptionsPageCountAtom,
  loadAmendmentSubscriptionsAtom,
  setAmendmentSubscriptionsPageAtom,
  setAmendmentSubscriptionsPerPageAtom,
  setAmendmentSubscriptionsResponsableAtom,
  setAmendmentSubscriptionsSearchAtom,
} from "../stores/subscriptions";
import type { AmendmentPerPage } from "../types";
import { AMENDMENT_PER_PAGE_CHOICES } from "../types";
import { AmendmentWeeksCell } from "./AmendmentWeeksCell";

const helper = createDataTableColumnHelper<ProjectRow>();

const SEARCH_DEBOUNCE = 300;

const EVERY_RESPONSABLE = "all";

export function AmendmentSubscriptionsTab(): JSX.Element {
  const { t } = useLingui();

  const {
    rows,
    total,
    page,
    perPage,
    search,
    responsable,
    responsables,
    loading,
    error,
  } = useAtomValue(amendmentSubscriptionsAtom);
  const pageCount = useAtomValue(amendmentSubscriptionsPageCountAtom);

  const load = useSetAtom(loadAmendmentSubscriptionsAtom);
  const setSearch = useSetAtom(setAmendmentSubscriptionsSearchAtom);
  const setResponsable = useSetAtom(setAmendmentSubscriptionsResponsableAtom);
  const setPerPage = useSetAtom(setAmendmentSubscriptionsPerPageAtom);
  const setPage = useSetAtom(setAmendmentSubscriptionsPageAtom);

  // What is typed, against what was asked for: the box answers every
  // keystroke, the server only the last one of a burst.
  const [typed, setTyped] = useState(search);

  const searchLater = useDebouncedCallback(
    (next: string) => setSearch(next),
    SEARCH_DEBOUNCE,
  );

  useEffect(() => {
    void load();
  }, [load]);

  const weeksStatusLabels = useMemo(
    () => ({
      missing: t({ message: "Missing", context: "closure weeks status" }),
      incomplete: t({ message: "Incomplete", context: "closure weeks status" }),
      complete: t({ message: "Complete", context: "closure weeks status" }),
      exceeding: t({ message: "Too many", context: "closure weeks status" }),
    }),
    [t],
  );

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("id", {
          header: t`Id`,
          enableSorting: false,
          meta: { minWidth: "70px", maxWidth: "90px" },
        }),
        helper.accessor("label", {
          header: t`Subscription`,
          enableSorting: false,
          meta: { minWidth: "160px", truncate: true },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.accessor("clientLabel", {
          header: t`Client`,
          enableSorting: false,
          meta: { minWidth: "140px", maxWidth: "260px", truncate: true },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.accessor("responsableName", {
          header: t`Responsable`,
          enableSorting: false,
          meta: { minWidth: "120px", maxWidth: "220px", truncate: true },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.accessor("canton", {
          header: t`Canton`,
          enableSorting: false,
          meta: { minWidth: "60px", maxWidth: "90px" },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.display({
          id: "weeks",
          header: t`Weeks`,
          enableSorting: false,
          meta: { minWidth: "90px", maxWidth: "130px" },
          cell: ({ row }) => {
            const weeks = row.original.closureWeeks;

            return (
              <AmendmentWeeksCell
                label={
                  weeks ? `${weeks.savedWeeks} / ${weeks.plannedWeeks}` : "-"
                }
                periods={weeks?.periods ?? []}
              />
            );
          },
        }),
        helper.display({
          id: "weeksStatus",
          header: t`Status`,
          enableSorting: false,
          meta: { minWidth: "110px", maxWidth: "150px" },
          cell: ({ row }) => {
            const weeks = row.original.closureWeeks;
            const status = weeks?.status;

            if (undefined === status) {
              return "-";
            }

            if (0 === weeks?.plannedWeeks) {
              return (
                <Badge
                  size="sm"
                  variant="warning"
                  icon="TriangleAlert"
                  title={t`The contract leaves no closure week this year: the amendments of its collaborateurs cannot be generated.`}
                >
                  {t({
                    message: "No week owed",
                    context: "closure weeks status",
                  })}
                </Badge>
              );
            }

            return (
              <Badge
                size="sm"
                variant={
                  "complete" === status
                    ? "success"
                    : "missing" === status
                      ? "error"
                      : "warning"
                }
              >
                {weeksStatusLabels[status]}
              </Badge>
            );
          },
        }),
      ]),
    [t, weeksStatusLabels],
  );

  return (
    <>
      {error ? (
        <p className={styles.alert} role="alert">
          {error}
        </p>
      ) : null}

      {/* Visible overflow: the pickers of the toolbar open out of the panel. */}
      <Panel overflow="visible">
        <Toolbar wrap={false}>
          <ToolbarSection className={styles.filters}>
            <Input
              className={styles.search}
              value={typed}
              placeholder={t`Search a subscription or an identifier`}
              leftSection={<Icon name="Search" size="sm" />}
              onValueChange={(next) => {
                setTyped(next);
                searchLater(next);
              }}
            />

            <Select
              items={[
                { label: t`Every responsable`, value: EVERY_RESPONSABLE },
                ...responsables.map((choice) => ({
                  label: choice.label,
                  value: String(choice.id),
                })),
              ]}
              value={
                null === responsable ? EVERY_RESPONSABLE : String(responsable)
              }
              onValueChange={(next) =>
                setResponsable(EVERY_RESPONSABLE === next ? null : Number(next))
              }
            >
              <SelectTrigger className={styles.filter}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent alignItemWithTrigger={false} popupWidth="content">
                <SelectItem value={EVERY_RESPONSABLE}>
                  {t`Every responsable`}
                </SelectItem>
                {responsables.map((choice) => (
                  <SelectItem key={choice.id} value={String(choice.id)}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ToolbarSection>

          <ToolbarSection>
            <Select
              items={AMENDMENT_PER_PAGE_CHOICES.map((size) => ({
                label: String(size),
                value: String(size),
              }))}
              value={String(perPage)}
              onValueChange={(next) =>
                setPerPage(Number(next) as AmendmentPerPage)
              }
            >
              <SelectTrigger className={styles.pageSize}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {AMENDMENT_PER_PAGE_CHOICES.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ToolbarSection>
        </Toolbar>

        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => String(row.id)}
          loading={loading}
          loadingRowCount={perPage}
          emptyMessage={
            <Trans>No subscription is concerned by a closure this year.</Trans>
          }
          columnBorders
          bordered={false}
          scroll="page"
          stickyHeader
          stickyOffset="var(--layout-header-height, 0px)"
        />
      </Panel>

      <Pagination
        page={page}
        pageCount={pageCount}
        totalItems={total}
        pageSize={perPage}
        align="end"
        onPageChange={(next) => setPage(next)}
      />
    </>
  );
}
